<template>
  <v-card
    v-if="calculator"
    class="white vetalfa-view"
    :max-width="_getMaxWidth()"
  >
    <div class="vetalfa-header" ref="screenHeader">
      <vetalfa-small-header
        :title="category ? category.portugueseName : null"
        :confirmationObject="{
          text: calculatorLabels.CONFIRMATION_QUIT_CALCULATOR,
          confirmText: calculatorLabels.QUIT_CALCULATOR,
          closeText: _getLabel('CANCEL'),
        }"
        hasShareOption
      ></vetalfa-small-header>
      <vetalfa-drugs-header
        class="mt-4"
        :verticalMargin="false"
        :name="calculator.name"
        :description="calculator.classes[0]"
        :displayCalculator="false"
        hasCircularProgress
      ></vetalfa-drugs-header>
    </div>

    <div ref="screenContent" style="height: 100%">
      <v-stepper v-model="stepModel">
        <v-stepper-items>
          <v-stepper-content step="1" class="ma-0 pa-0">
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('SELECT_PATIENT')"
              :collapse="false"
            >
              <animal-selector
                v-on:selectAnimal="selectAnimal"
                parentModelName="animal"
                :defaultValue="animal"
              >
              </animal-selector>
            </vetalfa-section-title>
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_PATIENT_WEIGHT')"
              :collapse="false"
            >
              <v-text-field
                v-bind:id="`numeric-weight`"
                type="text"
                hide-details
                inputmode="decimal"
                suffix="(kg)"
                class="mx-2 my-0 mb-5"
                light
                v-model="weight"
                dense
                color="primary"
                outlined
                :placeholder="
                  _getCalculatorsLabel('ENTER_PATIENT_WEIGHT_PLACEHOLDER')
                "
              ></v-text-field>
            </vetalfa-section-title>
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_CURRENT_HEMATOCRIT')"
              :collapse="false"
            >
              <v-text-field
                v-bind:id="`numeric-currentHematocrit`"
                type="text"
                hide-details
                inputmode="decimal"
                suffix="(%)"
                class="mx-2 my-0 mb-5"
                light
                v-model="currentHematocrit"
                dense
                color="primary"
                outlined
                :placeholder="_getCalculatorsLabel('ENTER_VOLUME')"
              ></v-text-field>
            </vetalfa-section-title>
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_INTENDED_HEMATOCRIT')"
              :collapse="false"
            >
              <v-text-field
                v-bind:id="`numeric-intendedHematocrit`"
                type="text"
                hide-details
                inputmode="decimal"
                suffix="(%)"
                class="mx-2 my-0 mb-5"
                light
                v-model="intendedHematocrit"
                dense
                color="primary"
                outlined
                :placeholder="_getCalculatorsLabel('ENTER_VOLUME')"
              ></v-text-field>
            </vetalfa-section-title>
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_DONOR_HEMATOCRIT')"
              :collapse="false"
            >
              <v-text-field
                v-bind:id="`numeric-donorHematocrit`"
                type="text"
                hide-details
                inputmode="decimal"
                suffix="(%)"
                class="mx-2 my-0 mb-5"
                light
                v-model="donorHematocrit"
                dense
                color="primary"
                outlined
                :placeholder="_getCalculatorsLabel('ENTER_VOLUME')"
              ></v-text-field>
            </vetalfa-section-title>
            <div style="height: 70px; background-color: white"></div>
          </v-stepper-content>

          <v-stepper-content step="2" class="ma-0 pa-0">
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_SPEED_AT_30_MINUTES')"
              :collapse="false"
            >
              <v-select
                v-bind:id="`numeric-firstSpeedTax`"
                :items="firstSpeedTaxItems"
                :placeholder="'Selecione'"
                hide-details
                :suffix="`(${_getCalculatorsLabel('UNIT_ML_KG_H')})`"
                filled
                class="mx-2 mb-5"
                dense
                outlined
                v-model="firstSpeedTax"
              >
                <template v-slot:item="{ item }">
                  <span>{{
                    `${item} ${_getCalculatorsLabel("UNIT_ML_KG_H")}`
                  }}</span>
                </template>
              </v-select>
            </vetalfa-section-title>
            <vetalfa-section-title
              :prop="_getCalculatorsLabel('ENTER_SPEED_AFTER_30_MINUTES')"
              :collapse="false"
            >
              <v-select
                v-bind:id="`numeric-secondSpeedTax`"
                :items="secondSpeedTaxItems"
                :placeholder="'Selecione'"
                hide-details
                :suffix="`(${_getCalculatorsLabel('UNIT_ML_KG_H')})`"
                filled
                class="mx-2 mb-5"
                dense
                outlined
                v-model="secondSpeedTax"
              >
                <template v-slot:item="{ item }">
                  <span>{{
                    `${item} ${_getCalculatorsLabel("UNIT_ML_KG_H")}`
                  }}</span>
                </template>
              </v-select>
            </vetalfa-section-title>
          </v-stepper-content>
          <v-stepper-content step="3" class="ma-0 pa-0">
            <div style="height: 100%" v-if="noResults">
              <vetalfa-section-title
                :prop="_getCalculatorsLabel('SOLUTION_COMPOSITION')"
              >
                <vetalfa-section-item
                  :prop="results.volume"
                ></vetalfa-section-item>
              </vetalfa-section-title>
              <vetalfa-section-title
                v-for="(speedItem, speedIndex) in results.speeds"
                :key="speedIndex + '-speed-item'"
                :prop="speedItem.title"
              >
                <vetalfa-section-item-title
                  :prop="_getCalculatorsLabel('SPEED')"
                >
                  <vetalfa-section-item
                    :prop="speedItem.speed"
                  ></vetalfa-section-item>
                </vetalfa-section-item-title>
                <vetalfa-section-item-title
                  :prop="_getCalculatorsLabel('MACRODROPS_SPEED')"
                >
                  <vetalfa-section-item
                    v-for="(obj, index) in speedItem.macroDrops"
                    :key="index + obj"
                    :prop="obj"
                    :index="index"
                  ></vetalfa-section-item>
                </vetalfa-section-item-title>
                <vetalfa-section-item-title
                  :prop="_getCalculatorsLabel('MICRODROPS_SPEED')"
                >
                  <vetalfa-section-item
                    v-for="(obj, index) in speedItem.microDrops"
                    :key="index + obj"
                    :prop="obj"
                    :index="index"
                  ></vetalfa-section-item>
                </vetalfa-section-item-title>
              </vetalfa-section-title>
              <vetalfa-general-tab
                :sections="calculator.sections"
                :references="calculator.references"
              ></vetalfa-general-tab>
              <div style="height: 70px; background-color: white"></div>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </div>
    <v-row
      class="ma-0 pa-0 white vetalfa-max-width"
      align="center"
      v-bind:style="`
        width: 100%;
        position: fixed;
        bottom: ${_isIos() ? 30 : 0}px;
        z-index: 10;
        height: 50px;
      `"
      justify="space-around"
    >
      <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 pl-2 pr-1 pb-2" align="center" justify="start">
          <v-btn
            v-show="stepModel == 2 || stepModel == 3"
            block
            outlined
            class="text-none primary--text"
            @click="stepModel -= 1"
          >
            Voltar
          </v-btn>
        </v-row>
      </v-col>
      <v-spacer></v-spacer>
      <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 pr-2 pl-1 pb-2" align="center" justify="end">
          <v-btn
            block
            v-show="stepModel < 3"
            :elevation="disabledStep ? 0 : 5"
            v-bind:class="
              disabledStep ? 'vetalfa-disabled-btn' : 'vetalfa-primary-btn'
            "
            class="text-none"
            @click="stepModel += disabledStep ? 0 : 1"
            light
          >
            {{
              stepModel == 2 ? _getLabel("SEE_RESULTS") : _getLabel("CONTINUE")
            }}
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<style>
.v-stepper {
  box-shadow: unset !important;
  -webkit-box-shadow: unset !important;
}
.v-stepper__header {
  box-shadow: unset !important;
  -webkit-box-shadow: unset !important;
}
</style>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import calculatorLabels from "@/constants/labels/calculators.labels.js";
import { mdiCheck, mdiPlus, mdiClose, mdiAlertCircleOutline } from "@mdi/js";
import imagesConstants from "@/constants/images.js";
import PieChart from "@/components/PieChart.js";
import AnimalSelector from "@/components/AnimalSelector.vue";
const stepperData = [
  { title: "Cálculo do Volume de Sangue" },
  { title: "Velocidade de Administração" },
  { title: "Resultados" },
];
export default {
  name: "ContinuousInfusionCalculator",
  components: {
    "pie-chart": PieChart,
    "animal-selector": AnimalSelector,
  },
  watch: {
    stepModel(val) {
      this.$vuetify.goTo(0, {
        duration: 0,
        offset: 0,
        easing: "easeInOutCubic",
      });
      if (val == 2) {
        this.transfusionBloodVolumeCalculate();
      } else if (val == 3) {
        this.calculateResults();
      }
    },
  },
  computed: {
    ...mapGetters("calculators", [
      "calculator",
      "getIntendedHematocrit",
      "getWeight",
      "getCurrentHematocrit",
      "getDonorHematocrit",
      "getAnimal",
    ]),
    ...mapGetters("general", ["getStepper"]),
    ...mapGetters("drugs", ["category"]),
    noResults() {
      return (
        !!this.results &&
        !!this.weight &&
        !!this.currentHematocrit &&
        !!this.intendedHematocrit &&
        !!this.donorHematocrit
      );
    },
    animal: {
      get() {
        return this.getAnimal;
      },
      set(val) {
        this.setAnimal(val);
      },
    },
    weight: {
      get() {
        return this.getWeight;
      },
      set(val) {
        this.setWeight(val);
      },
    },
    intendedHematocrit: {
      get() {
        return this.getIntendedHematocrit;
      },
      set(val) {
        this.setIntendedHematocrit(val);
      },
    },
    donorHematocrit: {
      get() {
        return this.getDonorHematocrit;
      },
      set(val) {
        this.setDonorHematocrit(val);
      },
    },
    currentHematocrit: {
      get() {
        return this.getCurrentHematocrit;
      },
      set(val) {
        this.setCurrentHematocrit(val);
      },
    },
    stepModel: {
      get() {
        return this.getStepper;
      },
      set(val) {
        this.setStepper(val);
      },
    },
    disabledStep() {
      return (
        (this.stepModel == 1 &&
          (!this.weight ||
            !this.currentHematocrit ||
            !this.intendedHematocrit ||
            !this.donorHematocrit)) ||
        (this.stepModel == 2 && (!this.firstSpeedTax || !this.secondSpeedTax))
      );
    },
  },
  methods: {
    ...mapMutations("general", [
      "setStepper",
      "setStepperTotal",
      "setStepperData",
    ]),
    ...mapMutations("calculators", [
      "setIntendedHematocrit",
      "setDonorHematocrit",
      "setCurrentHematocrit",
      "setWeight",
      "setAnimal",
    ]),
    ...mapActions("calculators", ["openCalculatorById"]),
    ...mapActions("general", ["showError"]),
    transfusionBloodVolumeCalculate() {
      this.firstStepResult = this.calculator.transfusionBloodVolumeCalculate({
        animal: this.animal,
        weight: this.weight,
        currentHematocrit: this.currentHematocrit,
        intendedHematocrit: this.intendedHematocrit,
        donorHematocrit: this.donorHematocrit,
      });
      if (this.firstStepResult.type == "error") {
        this.stepModel = 1;
        this.showError(this.firstStepResult.description);
      }
    },
    calculateResults() {
      this.results = {};
      this.results["volume"] = this.firstStepResult;
      const firstSpeed =
        this._getNumber(this.weight) *
        new Number((this.firstSpeedTax || "0").replace(",", "."));
      let firstSpeedObject = {};
      firstSpeedObject["title"] =
        "Velocidade de Administração nos 30 primeiros minutos";
      firstSpeedObject["speed"] =
        this._formatNumber(this._roundNumber(firstSpeed, 2)) + " mL/hora";
      firstSpeedObject["macroDrops"] = [];
      firstSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed / 180, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_SECONDS")}`
      );
      firstSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed / 18, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_TEN_SECONDS")}`
      );
      firstSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed / 3, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_MINUTES")}`
      );
      firstSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(180 / firstSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_SECONDS_DROPS")}`
      );
      firstSpeedObject["microDrops"] = [];
      firstSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed / 60, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_SECONDS")}`
      );
      firstSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed / 6, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_TEN_SECONDS")}`
      );
      firstSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(firstSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_MINUTES")}`
      );
      firstSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(60 / firstSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_SECONDS_DROPS")}`
      );

      const secondSpeed =
        this._getNumber(this.weight) *
        new Number((this.secondSpeedTax || "0").replace(",", "."));
      let secondSpeedObject = {};
      secondSpeedObject["title"] =
        "Velocidade de Administração após os 30 primeiros minutos";
      secondSpeedObject["speed"] =
        this._formatNumber(this._roundNumber(secondSpeed, 2)) + " mL/hora";
      secondSpeedObject["macroDrops"] = [];
      secondSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed / 180, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_SECONDS")}`
      );
      secondSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed / 18, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_TEN_SECONDS")}`
      );
      secondSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed / 3, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_MINUTES")}`
      );
      secondSpeedObject["macroDrops"].push(
        `${this._formatNumber(
          this._roundNumber(180 / secondSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_SECONDS_DROPS")}`
      );
      secondSpeedObject["microDrops"] = [];
      secondSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed / 60, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_SECONDS")}`
      );
      secondSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed / 6, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_TEN_SECONDS")}`
      );
      secondSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(secondSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_DROPS_MINUTES")}`
      );
      secondSpeedObject["microDrops"].push(
        `${this._formatNumber(
          this._roundNumber(60 / secondSpeed, 2)
        )} ${this._getCalculatorsLabel("UNIT_SECONDS_DROPS")}`
      );
      this.results["speeds"] = [firstSpeedObject, secondSpeedObject];
    },
    selectAnimal(val) {
      this.animal = val.value;
    },
    parseNumberField(e) {
      const id = e.srcElement.id.replace("numeric", "");
      const { value } = this._formatNumberField(e, this[id]);
      this[id] = value;
    },
    setupVariables() {
      [
        "weight",
        "currentHematocrit",
        "donorHematocrit",
        "intendedHematocrit",
        "firstSpeedTax",
        "secondSpeedTax",
      ].forEach((n) => {
        let el = document.getElementById(`numeric-${n}`);
        el.value = 0;
        el.addEventListener("input", this.parseNumberField);
        el.addEventListener("focus", this.parseNumberField);
        el.addEventListener("blur", this.parseNumberField);
      });
    },
  },
  data() {
    return {
      noDrugsImage: imagesConstants.NO_RESULTS,
      calculatorLabels,
      mdiCheck,
      mdiPlus,
      mdiClose,
      mdiAlertCircleOutline,
      headerHeight: 0,
      results: null,
      chartData: null,
      firstSpeedTax: null,
      secondSpeedTax: null,
      firstSpeedTaxItems: ["0,25", "0,5", "1,0", "2,0", "4,0"],
      secondSpeedTaxItems: [
        { header: "Normovolêmico" },
        "3,0",
        "4,5",
        "6,0",
        "8,0",
        { header: "Hipovolêmico" },
        "10",
        "15",
        "20",
        { header: "Risco de sobrecarga de volume (Cardiopatas e Nefropatas)" },
        "1,0",
        "2,0",
        "4,0",
        "5,0",
      ],
      firstStepResult: null,
      chartData: null,
    };
  },
  mounted() {
    if (this.calculator) {
      this.setupVariables();
      this._setupScreenSizes(this);
    }
  },
  created: async function () {
    if (
      !this.calculator ||
      this.calculator.id !== calculatorLabels.TRANSFUSION_BLOOD_AND_SPEED_ID
    ) {
      await this.openCalculatorById({
        id: calculatorLabels.TRANSFUSION_BLOOD_AND_SPEED_ID,
        redirect: false,
      });
      this.setupVariables();
      this._setupScreenSizes(this);
    }
    this.setStepper(1);
    this.setStepperTotal(3);
    this.setWeight(null);
    this.setCurrentHematocrit(null);
    this.setDonorHematocrit(null);
    this.setIntendedHematocrit(null);
    this.setAnimal(0);
    this.setStepperData(stepperData);
  },
};
</script>
